import React, { useState, useMemo } from "react";
import api from "@/lib/api";
import {
    BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, Legend, AreaChart, Area, ComposedChart,
} from "recharts";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Slider } from "@/components/ui/slider";
import { ChevronDown, Check, Filter, FilterX, MoreVertical, EyeOff, Trash2 } from "lucide-react";
import { toast } from "sonner";
import GeoMapWidget from "@/components/dashboards/GeoMapWidget";
// iter-121.104 — Canonical legend/tooltip resolution (see comment in helper).
import { deriveLegendName, deriveTooltipName } from "@/lib/widget-derivations";
// iter-121.132 — Theme-aware chart palette. Was a hardcoded hex array;
// reading from chartFormat.CHART_COLORS lets the prebuilt single-/multi-
// series renderers pick up the tenant's --chart-N CSS vars set by the
// Settings → Dashboard Theme picker. Without this, prebuilt charts
// always rendered in the brand-orange palette even when the SA chose
// Teal / Slate / Custom palettes.
import { CHART_COLORS, PIE_COLORS } from "@/components/dashboards/chartFormat";
import { chronoTickPlan, formatDayLabel, ROTATED_AXIS_HEIGHT } from "@/lib/chrono-ticks";

/**
 * Prebuilt-dashboard presentation primitives — extracted from LockedKpiStrip
 * during the iter-73 refactor to keep the orchestrator under 1000 lines and
 * allow these components to be unit-tested / reused independently.
 *
 * Purely presentational — no backend state, no side effects beyond the
 * FilterControl's `distinct` lookup for multi_select_dynamic filters.
 */

const PALETTE = CHART_COLORS;


/** Format a numeric KPI value for display (K / M abbreviations, integer comma, 2-dp decimal).
 *  iter-121.51 — honour `widget.format` patch from Sense AI Builder. Supports:
 *    • format.decimals (default 2 for fractional, 0 for integers)
 *    • format.currency (prefixes "$")
 *    • format.style: "raw" | "abbreviated" (default abbreviated for KPI tiles).
 */
export function formatKpi(v, format) {
    if (typeof v !== "number") return String(v);
    const dec = (format && Number.isFinite(format.decimals)) ? Math.max(0, Math.min(6, format.decimals)) : 2;
    const cur = !!(format && format.currency);
    const isPercent = !!(format && (format.percent || format.percentage || format.style === "percent" || format.style === "percentage"));
    const style = (format && format.style) || "abbreviated";
    const prefix = cur ? "$" : "";
    let suffix = (format && format.suffix) || "";
    if (isPercent && !suffix) suffix = "%";
    let displayVal = v;
    if (isPercent && Math.abs(v) <= 1 && v !== 0) {
        displayVal = v * 100;
    }
    if (style === "raw") {
        if (Number.isInteger(displayVal) && !cur && !isPercent) return prefix + displayVal.toLocaleString() + suffix;
        return prefix + displayVal.toLocaleString(undefined, { minimumFractionDigits: dec, maximumFractionDigits: dec }) + suffix;
    }
    const abs = Math.abs(displayVal);
    let valStr = "";
    if (abs >= 1_000_000_000) valStr = (displayVal / 1_000_000_000).toFixed(dec) + "B";
    else if (abs >= 1_000_000) valStr = (displayVal / 1_000_000).toFixed(dec) + "M";
    else if (abs >= 1_000) valStr = (displayVal / 1_000).toFixed(dec) + "K";
    else if (Number.isInteger(displayVal) && !cur && !isPercent) valStr = displayVal.toLocaleString();
    else valStr = displayVal.toLocaleString(undefined, { minimumFractionDigits: dec, maximumFractionDigits: dec });
    return prefix + valStr + suffix;
}


/**
 * iter-121.51 — Tooltip/value formatter for chart tooltips and table cells.
 * Forces full precision (no K/M abbreviation) but honours format.decimals
 * (default 2 dp), format.currency, and format.percent.
 * ISSUE-FIX: Support percentage formatting for calculated ratios and rates in table cells.
 */
function formatNumberRaw(v, format) {
    if (typeof v !== "number" || !Number.isFinite(v)) return String(v ?? "—");
    const dec = (format && Number.isFinite(format.decimals)) ? Math.max(0, Math.min(6, format.decimals)) : 2;
    const cur = !!(format && format.currency);
    const isPercent = !!(format && (format.percent || format.percentage || format.style === "percent" || format.style === "percentage"));
    const prefix = cur ? "$" : "";
    let suffix = (format && format.suffix) || "";
    if (isPercent && !suffix) suffix = "%";
    let displayVal = v;
    if (isPercent && Math.abs(v) <= 1 && v !== 0) {
        displayVal = v * 100;
    }
    if (Number.isInteger(displayVal) && !cur && !isPercent) return prefix + displayVal.toLocaleString() + suffix;
    return prefix + displayVal.toLocaleString(undefined, { minimumFractionDigits: dec, maximumFractionDigits: dec }) + suffix;
}


/**
 * iter-121.104 — Legend + tooltip derivation moved into the shared
 * `lib/widget-derivations.js` helper so any future renderer
 * automatically inherits the precedence ladder. The previous in-file
 * implementation lived here; keeping the alias names for back-compat
 * with the call sites below.
 */
const deriveSeriesName = deriveLegendName;


/** Truncate a category label for bar/line tick rendering; tooltip keeps full text. */
function truncTick(v) {
    const s = String(v ?? "");
    return s.length > 14 ? s.slice(0, 12) + "…" : s;
}


/**
 * iter-119ab (G5) — Per-widget filter-override toggle.
 *
 * Renders a tiny icon button on chart / table card headers that flips
 * the widget's `ignore_strip_filters` flag. When OFF (default), the
 * widget inherits the strip's date/source/dynamic filters. When ON,
 * the widget renders against the unfiltered model — useful for
 * sentinel KPIs like "Lifetime customers" that should NOT change
 * when the dashboard-wide date filter moves.
 *
 * Visibility: only renders when `dashboardId` is provided. Persists
 * via `POST /api/dashboards/{id}/widgets/{wid}/filter-override`. On
 * success, dispatches `dashboard:refresh` so the strip re-queries
 * immediately.
 */
function WidgetFilterOverrideToggle({ widget, dashboardId, onDashboardUpdated }) {
    const [busy, setBusy] = useState(false);
    const [override, setOverride] = useState(!!widget?.ignore_strip_filters);
    if (!dashboardId || !widget?.id) return null;
    const toggle = async () => {
        const next = !override;
        setBusy(true);
        try {
            await api.post(`/dashboards/${dashboardId}/widgets/${widget.id}/filter-override`,
                { ignore_strip_filters: next });
            setOverride(next);
            toast.success(next ? "Filter inheritance disabled" : "Filter inheritance re-enabled");
            try {
                const rDash = await api.get(`/dashboards/${dashboardId}`);
                if (onDashboardUpdated && rDash.data) {
                    onDashboardUpdated(rDash.data);
                }
            } catch (err) { /* ignore dashboard fetch error, fallback to refresh event */ }
            // Trigger a strip-wide refresh so the chart re-renders with
            // the new filter behaviour without a page reload.
            window.dispatchEvent(new CustomEvent("dashboard:refresh", {
                detail: { id: dashboardId },
            }));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not update widget override");
        } finally {
            setBusy(false);
        }
    };
    return (
        <button
            onClick={toggle}
            disabled={busy}
            className={`inline-flex items-center gap-1 h-6 px-1.5 rounded-sm border text-[10px] font-mono uppercase tracking-wider transition-colors ${override
                ? "border-amber-500/60 text-amber-600 dark:text-amber-400 bg-amber-500/5"
                : "border-border text-muted-foreground hover:border-foreground/40"
                }`}
            title={override
                ? "This widget IGNORES strip filters. Click to re-enable inheritance."
                : "This widget INHERITS strip filters. Click to ignore them for this tile."}
            data-testid={`widget-filter-override-${widget.id}`}
        >
            {override ? <FilterX className="h-2.5 w-2.5" /> : <Filter className="h-2.5 w-2.5" />}
            {override ? "NO FILTERS" : "FILTERS"}
        </button>
    );
}


/**
 * iter-121.132 — Widget action menu (...) for non-KPI prebuilt cards.
 *
 * Recurring user request: KPI tiles have a (...) menu with Hide/Restore;
 * charts + tables + maps did NOT. Now every non-KPI widget on a prebuilt
 * dashboard carries the same menu. Two items:
 *
 *   • "Hide from my view"   — visible to EVERYONE. Per-user (writes to
 *     dashboard_user_preferences.hidden_widgets); reversible from the
 *     pool. Doesn't affect other viewers.
 *   • "Remove from prebuilt" — visible to SUPER ADMIN only. Permanent
 *     delete from the underlying gallery_items template. Routes
 *     through DELETE /dashboards/{id}/widgets/{wid} which reuses the
 *     Sense AI Builder's _tool_remove_widget so propagation +
 *     activity-log behaviour is identical to an AI-driven remove.
 *
 * Pure presentation — caller passes `onHide` (always defined) and
 * `onRemoveFromPrebuilt` (only defined when the caller knows the
 * current user is SA). When `onRemoveFromPrebuilt` is omitted the
 * second item is hidden.
 */
export function WidgetActionMenu({ widget, onHide, onRemoveFromPrebuilt }) {
    const [open, setOpen] = useState(false);
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    aria-label="Widget actions"
                    data-testid={`widget-actions-${widget?.id || "unknown"}`}
                    className="p-1 rounded hover:bg-secondary/40 opacity-60 hover:opacity-100 transition"
                    onClick={(e) => e.stopPropagation()}
                >
                    <MoreVertical className="h-3.5 w-3.5" />
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="end"
                sideOffset={4}
                className="w-44 p-1"
                onClick={(e) => e.stopPropagation()}
            >
                {onHide && (
                    <button
                        type="button"
                        data-testid={`widget-hide-${widget?.id || "unknown"}`}
                        className="w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded hover:bg-secondary/40 text-left"
                        onClick={() => { setOpen(false); onHide(widget?.id); }}
                    >
                        <EyeOff className="h-3.5 w-3.5 opacity-70" />
                        Hide from my view
                    </button>
                )}
                {onRemoveFromPrebuilt && (
                    <button
                        type="button"
                        data-testid={`widget-remove-prebuilt-${widget?.id || "unknown"}`}
                        className="w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded hover:bg-destructive/10 text-destructive text-left"
                        onClick={() => { setOpen(false); onRemoveFromPrebuilt?.(widget?.id); }}
                    >
                        <Trash2 className="h-3.5 w-3.5" />
                        Remove from prebuilt
                    </button>
                )}
            </PopoverContent>
        </Popover>
    );
}


const ChronoTick = ({ x, y, payload, data, width, index }) => {
    if (!payload) return null;
    const value = payload.value;
    const s = String(value || "");
    const matchDaily = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);

    if (!matchDaily) {
        return (
            <text x={x} y={y + 10} textAnchor="middle" fontSize={10} fill="hsl(var(--muted-foreground))">
                {value}
            </text>
        );
    }
    if (!Array.isArray(data)) return null;
    const dataIndex = typeof index === "number" ? index : data.findIndex(d => d?.label === value);
    if (dataIndex === -1) return null;
    const { rotated, show } = chronoTickPlan(data, width);
    if (!show(dataIndex)) return null;
    const label = formatDayLabel(value);
    return (
        <g transform={`translate(${x},${y})`}>
            <text x={0} y={rotated ? 6 : 10} dy={rotated ? 4 : 0} textAnchor={rotated ? "end" : "middle"}
                  transform={rotated ? "rotate(-45)" : undefined} fontSize={9} fill="hsl(var(--muted-foreground))">
                {label}
            </text>
        </g>
    );
};


export function PrebuiltChartCard({ widget, liveData, timeGrain, dashboardId, onHide, onRemoveFromPrebuilt, onResize, currentSize, onDashboardUpdated }) {
    const data = useMemo(() => {
        if (liveData && !Array.isArray(liveData) && Array.isArray(liveData.data)) {
            return liveData.data;
        }
        return liveData || [];
    }, [liveData]);

    const groups = useMemo(() => {
        if (liveData && !Array.isArray(liveData) && Array.isArray(liveData.groups)) {
            return liveData.groups;
        }
        return widget?.groups || widget?.config?.groups || [];
    }, [liveData, widget]);

    const hasGroups = groups.length > 0;
    const isLine = widget.chart_type === "line";
    const isDonut = widget.chart_type === "donut" || widget.chart_type === "pie";
    const isGeoMap = widget.chart_type === "geo_map";
    const isArea = widget.chart_type === "area";
    const isBar = widget.chart_type === "bar";
    const isHorizontalBar = widget.chart_type === "horizontal_bar";
    const isCombo = widget.chart_type === "combo" || widget.chart_type === "composed";
    const isTimeChart = isLine || isBar || isHorizontalBar || isArea || isCombo;
    // iter-121.128c — Multi-series detection. A widget carrying a non-
    // empty `series` array (validated in `_tool_add_widget` so each
    // entry already has {name, formula, color?}) renders ONE
    // <Line>/<Bar>/<Area> per series. We pull `series_names` from the
    // resolver payload when available so the dataKey order matches what
    // the backend actually keyed the rows under — defensive against any
    // future series reordering by the LLM after the data was fetched.
    const seriesDefs = Array.isArray(widget?.series) ? widget.series : [];
    const isMultiSeries = (seriesDefs.length > 1 && isTimeChart) || isCombo;
    // iter-121.61 — `y_is_currency` reference was undefined and crashed
    // the prebuilt preview page (`Uncaught ReferenceError: y_is_currency
    // is not defined` at PrebuiltChartCard). Pull it from the widget
    // (or its config / format) so the tooltip formatter & geo map can
    // render currency-aware values when the template authored that flag.
    const y_is_currency = Boolean(
        widget?.y_is_currency
        ?? widget?.config?.y_is_currency
        ?? widget?.format?.currency
        ?? widget?.format?.is_currency
        ?? false
    );
    // iter-119aa (G3) — client-side time-grain rebucket for line/bar/area charts.
    // Only applies when:
    //   • The chart is a line/bar/area (time-series) AND
    //   • Labels parse cleanly as ISO dates (YYYY-MM-DD) so we can
    //     compute a parent bucket safely.
    // Falls through to raw data otherwise — never breaks non-time charts.
    // iter-121.128c — Preserve every numeric key when rebucketing so
    // multi-series rows (e.g. {label, Spend, Revenue}) keep both metrics
    // across D → W → M. Previously only `value` was summed, which silently
    // dropped every series except the legacy single-value one.
    const displayData = React.useMemo(() => {
        if (!isTimeChart || !timeGrain || timeGrain === "auto" || timeGrain === "day") return data;
        const _isoBucket = (lbl) => {
            if (lbl == null || lbl === "") return null;
            // iter-120 round-4 · Only attempt to parse strings that
            // look like ISO dates / months — `new Date("foo")` happily
            // returns Invalid Date but `new Date("2026-05")` parses
            // as 2026-05-01, so we need a regex gate to avoid burning
            // every non-date label into the same Jan-1970 bucket.
            const s = String(lbl);
            if (!/^\d{4}-\d{2}(-\d{2})?(T.*)?$/.test(s)) return null;
            const d = new Date(s);
            if (Number.isNaN(d.getTime())) return null;
            if (timeGrain === "week") {
                const day = (d.getUTCDay() + 6) % 7;
                d.setUTCDate(d.getUTCDate() - day);
                return d.toISOString().slice(0, 10);
            }
            if (timeGrain === "month") return d.toISOString().slice(0, 7);
            return null;
        };
        // iter-120 round-4 · Skip un-bucketable rows instead of bailing
        // the entire rebucket (was: any null-labeled row dumped us back
        // to raw data, which made D/W/M silently a no-op on datasets
        // where some rows had missing date values — exactly the bug
        // SA saw on Marketing Performance).
        const buckets = {};
        let bucketed = 0;
        for (const row of data) {
            const b = _isoBucket(row.label);
            if (!b) continue;
            if (!buckets[b]) buckets[b] = { label: b };
            for (const [k, v] of Object.entries(row)) {
                if (k === "label" || typeof v !== "number") continue;
                buckets[b][k] = (buckets[b][k] || 0) + v;
            }
            bucketed++;
        }
        if (bucketed === 0) return data;
        const sd = widget?.sort_dir || widget?.config?.sort_dir;
        const isDesc = sd && sd.toLowerCase().startsWith("desc");
        return Object.values(buckets).sort((a, b) => {
            return isDesc ? b.label.localeCompare(a.label) : a.label.localeCompare(b.label);
        });
    }, [data, isTimeChart, timeGrain, widget]);

    // iter-120 · Chart click → drill-down. Emits a `dashboard:chart-drill`
    // window event the parent LockedKpiStrip listens for to open the
    // KpiDrillDownDialog filtered to the clicked group_by value. Works
    // for bar / donut / line charts uniformly — purely chart_type
    // driven, no hardcoded column-name special-cases.
    const _emitDrill = (label, seriesName) => {
        if (label == null) return;
        window.dispatchEvent(new CustomEvent("dashboard:chart-drill", {
            detail: { widget, label: String(label), seriesName, dashboardId },
        }));
    };
    const handleBarClick = (entry, seriesName) => entry && _emitDrill(entry.label, seriesName);
    const handleSliceClick = (entry) => entry && _emitDrill(entry.label);
    // iter-121.51 — Recharts passes (props, event) to activeDot.onClick;
    // the prior `(_e, payload)` order swallowed the click on the line.
    // We coerce defensively so either signature lands on the label.
    const handlePointClick = (a, b, g) => {
        const payload = (a && a.payload) ? a : (b && b.payload) ? b : null;
        const label = payload?.payload?.label ?? payload?.label;
        const groupName = g || payload?.name;
        if (label != null) _emitDrill(label, groupName);
    };
    // iter-120 round-3 · Geo map now spans 3 cols on xl (full row).
    // With the extra width, bump height to 460 so the map projection
    // doesn't waste vertical space at the bottom of a wide card.
    const baseHeight = isGeoMap ? 230 : 150;
    const chartHeight = baseHeight * (widget.rowspan || 2);
    // Common legend renderer — used by bar/line/donut alike per
    // the user request "legends should be for all chart types".
    // For bar/line we render a top-aligned legend that shows the
    // single series colour + name; for donut we keep the right-
    // aligned vertical legend (one entry per slice).
    // iter-121.51 — Use deriveSeriesName so compound formulas don't
    // leak into the legend (`spend)/SUM(m.conversions` bug).
    const seriesName = deriveSeriesName(widget);
    const tooltipName = deriveTooltipName(widget);
    const fmt = widget.format && typeof widget.format === "object" ? widget.format : undefined;
    const yIsCurrency = y_is_currency || (fmt && fmt.currency);
    // iter-121.104 — Recharts Tooltip formatter signature is
    // `(value, name, props) => [displayValue, displayName?]`.
    // Returning a tuple lets us rewrite the dataKey ("value") with
    // the widget's tooltip label without touching the legend chip.
    //
    // iter-121.129b — Multi-series fix. For a multi-series chart each
    // hovered point belongs to ONE series; the formatter is invoked
    // PER series with the series `name` already passed by Recharts. We
    // must surface that series-specific name (e.g. "Spend", "Revenue")
    // rather than the widget-level `tooltip_label`, otherwise every
    // hovered line shows the SAME label and the user can't tell them
    // apart — exactly the "hover labels not updating" regression.
    const tooltipFmt = (v, name, _props) => {
        const isPct = name && (name.toLowerCase().includes("yoy") || name.toLowerCase().includes("%") || name.toLowerCase().includes("growth"));
        const display = isPct
            ? `${formatNumberRaw(v, { ...(fmt || {}), currency: false, decimals: 1 })}%`
            : formatNumberRaw(v, { ...(fmt || {}), currency: !!yIsCurrency });
        return [display, (isMultiSeries || hasGroups) ? (name || tooltipName) : tooltipName];
    };
    const isChrono = Array.isArray(displayData) && displayData.some(d => d && d.label && /^\d{4}-\d{2}-\d{2}/.test(String(d.label)));
    const xAxisProps = isChrono ? { tick: <ChronoTick data={displayData} />, angle: 0, textAnchor: "middle", height: ROTATED_AXIS_HEIGHT, interval: 0 } : {};
    const tickFormatterProps = isChrono ? {} : { tickFormatter: truncTick };

    const isEmpty = !displayData || displayData.length === 0 || displayData.every((row) => {
        if (isMultiSeries) {
            return seriesDefs.every((s) => {
                const val = row[s.name];
                return val === undefined || val === null || val === "" || Number(val) === 0;
            });
        } else if (hasGroups) {
            return groups.every((g) => {
                const val = row[g];
                return val === undefined || val === null || val === "" || Number(val) === 0;
            });
        } else {
            const val = row.value;
            return val === undefined || val === null || val === "" || Number(val) === 0;
        }
    });
    return (
        <div className="dash-card p-4 flex flex-col" data-testid={`prebuilt-chart-${widget.id}`}>
            <div className="flex items-center justify-between mb-2 gap-2">
                <div className="text-[10px] font-mono uppercase tracking-wider opacity-70">{widget.title}</div>
                <div className="flex items-center gap-1">
                    <WidgetFilterOverrideToggle widget={widget} dashboardId={dashboardId} onDashboardUpdated={onDashboardUpdated} />
                    {/* iter-121.132 — Action menu (Hide / Remove from prebuilt). */}
                    {(onHide || onRemoveFromPrebuilt) && (
                        <WidgetActionMenu
                            widget={widget}
                            currentSize={currentSize}
                            onResize={onResize}
                            onHide={onHide}
                            onRemoveFromPrebuilt={onRemoveFromPrebuilt}
                        />
                    )}
                </div>
            </div>
            <div style={{ width: "100%", height: chartHeight, minHeight: chartHeight }}>
                {/* {isGeoMap ? ( */}
                {isEmpty ? (
                    <div className="flex items-center justify-center h-full text-xs text-muted-foreground">
                        No data
                    </div>
                ) : isGeoMap ? (
                    <GeoMapWidget w={{
                        id: widget.id,
                        title: widget.title,
                        group_by: widget.group_by,
                        x_field: widget.x_field,
                        geo_level: liveData?.geo_level || widget.geo_level,
                        geo_country_field: liveData?.geo_country_field || widget.geo_country_field,
                        geo_admin1_url: widget.geo_admin1_url,
                        options: widget.options,
                        config: {
                            chart_type: "geo_map",
                            data: displayData,
                            y_is_currency: !!yIsCurrency,
                            geo_level: liveData?.geo_level || widget.geo_level,
                            geo_country_field: liveData?.geo_country_field || widget.geo_country_field,
                            geo_admin1_url: widget.geo_admin1_url,
                        },
                    }} noCard={true} />
                ) : (
                    <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                        {isDonut ? (
                            <PieChart>
                                <Pie data={displayData} dataKey="value" nameKey="label" cx="38%" cy="50%"
                                    innerRadius={widget.chart_type === "donut" ? "50%" : "0%"} outerRadius="80%" paddingAngle={2}
                                    onClick={handleSliceClick} cursor="pointer">
                                    {displayData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                                </Pie>
                                <Tooltip
                                    content={({ active, payload }) => {
                                        if (!active || !payload || !payload.length) return null;
                                        const item = payload[0];
                                        const display = formatNumberRaw(item.value, { ...(fmt || {}), currency: !!yIsCurrency });
                                        return (
                                            <div className="border border-border bg-card p-2 rounded shadow-sm text-xs">
                                                <div className="font-normal mb-0.5 text-foreground">{item.name}</div>
                                                <div className="flex items-center gap-1" style={{ color: item.color || "hsl(var(--primary))" }}>
                                                    <span>{tooltipName}</span>
                                                    <span>:</span>
                                                    <span className="font-medium">{display}</span>
                                                </div>
                                            </div>
                                        );
                                    }}
                                />
                                <Legend layout="vertical" align="right" verticalAlign="middle"
                                    iconType="circle" iconSize={9}
                                    wrapperStyle={{ fontSize: 11, lineHeight: "16px", paddingLeft: 8, maxWidth: "45%" }}
                                    formatter={(v) => (typeof v === "string" && v.length > 18 ? v.slice(0, 18) + "…" : v)} />
                            </PieChart>
                        ) : isCombo ? (
                            <ComposedChart data={displayData} margin={{ top: 5, right: 8, left: 0, bottom: 28 }}
                                onClick={(state) => {
                                    const lbl = state?.activeLabel ?? state?.activePayload?.[0]?.payload?.label;
                                    if (lbl != null) _emitDrill(lbl);
                                }}>
                                <CartesianGrid strokeDasharray="2 4" stroke="rgba(127,127,127,0.18)" />
                                <XAxis dataKey="label" fontSize={10} stroke="currentColor" interval={0} angle={-30} textAnchor="end" height={56} {...tickFormatterProps} {...xAxisProps} />
                                <YAxis yAxisId="left" fontSize={10} stroke="currentColor" tickFormatter={(v) => formatKpi(v, fmt)} />
                                {/* ISSUE-FIX: Format the right Y-axis dynamically based on the metric type of the series mapped to it */}
                                <YAxis yAxisId="right" orientation="right" fontSize={10} stroke="currentColor" tickFormatter={(v) => {
                                    const rightSeries = seriesDefs.find(s => s.y_axis === "right");
                                    const isPercent = rightSeries && (rightSeries.unit === "%" || (rightSeries.format && (rightSeries.format.is_percent || rightSeries.format.style === "percent")));
                                    if (isPercent) return `${v}%`;
                                    return formatKpi(v, rightSeries?.format || { decimals: 0 });
                                }} />
                                <Tooltip contentStyle={{ fontSize: 12 }} formatter={tooltipFmt} labelFormatter={(l) => String(l)} />
                                <Legend verticalAlign="top" height={20} iconType="rect" iconSize={10} wrapperStyle={{ fontSize: 11, paddingBottom: 4 }} />
                                {seriesDefs.map((s, i) => {
                                    const yAxisId = s.y_axis === "right" ? "right" : "left";
                                    const color = s.color || PALETTE[i % PALETTE.length];
                                    if (s.render_as === "line") {
                                        return (
                                            <Line key={s.name || i} yAxisId={yAxisId} type="monotone" dataKey={s.name} name={s.name}
                                                stroke={color} strokeWidth={2} dot={false}
                                                /* ISSUE-FIX: Pass series name to point click handler in ComposedChart */
                                                activeDot={{ r: 5, onClick: (a, b) => handlePointClick(a, b, s.name), cursor: "pointer" }} />
                                        );
                                    } else {
                                        return (
                                            <Bar key={s.name || i} yAxisId={yAxisId} dataKey={s.name} name={s.name}
                                                fill={color} radius={[2, 2, 0, 0]}
                                                /* ISSUE-FIX: Pass explicit series name to handleBarClick in ComposedChart */
                                                onClick={(entry) => handleBarClick(entry, s.name)} cursor="pointer"
                                                /* ISSUE-FIX: Support stacking multiple series in ComposedChart (combo chart) if options/config specify stacked. Resolves the bug where stacked combo charts showed side-by-side bars. */
                                                stackId={widget.options?.stacked || widget.config?.stacked || widget.stacked ? "a" : undefined} />
                                        );
                                    }
                                })}
                            </ComposedChart>
                        ) : isLine ? (
                            <LineChart data={displayData} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}
                                onClick={(state) => {
                                    // iter-121.51 — fallback click handler on the LineChart itself
                                    // so a click anywhere near a data point still triggers drill,
                                    // even when activeDot interception flakes (Recharts internal
                                    // event chain differs by version).
                                    const lbl = state?.activeLabel ?? state?.activePayload?.[0]?.payload?.label;
                                    if (lbl != null) _emitDrill(lbl);
                                }}>
                                <CartesianGrid strokeDasharray="2 4" stroke="rgba(127,127,127,0.18)" />
                                <XAxis dataKey="label" fontSize={10} stroke="currentColor" {...tickFormatterProps} {...xAxisProps} />
                                <YAxis fontSize={10} stroke="currentColor" tickFormatter={(v) => formatKpi(v, fmt)} />
                                <Tooltip contentStyle={{ fontSize: 12 }} formatter={tooltipFmt}
                                    labelFormatter={(l) => String(l)} />
                                {/* iter-120 · top-aligned legend for line/bar
                                so users see the metric name (Spend,
                                Conversions, …) up-front. Single-series
                                charts get a legend too — explicit > magical. */}
                                <Legend verticalAlign="top" height={20} iconType="rect" iconSize={10}
                                    wrapperStyle={{ fontSize: 11, paddingBottom: 4 }} />
                                {/* iter-121.128c — Multi-series rendering. When
                                `widget.series` carries 2+ entries, emit one
                                <Line> per series using the series name as
                                the dataKey (matches the resolver's row
                                shape). Single-series widgets fall back to
                                the legacy `dataKey="value"` path so every
                                existing dashboard keeps rendering identically. */}
                                {isMultiSeries ? (
                                    seriesDefs.map((s, i) => (
                                        <Line key={s.name || i} type="monotone" dataKey={s.name}
                                            name={s.name}
                                            stroke={s.color || PALETTE[i % PALETTE.length]}
                                            strokeWidth={2} dot={false}
                                            /* ISSUE-FIX: Pass explicit series name to handlePointClick in multi-series LineChart */
                                            activeDot={{ r: 5, onClick: (a, b) => handlePointClick(a, b, s.name), cursor: "pointer" }} />
                                    ))
                                ) : hasGroups ? (
                                    groups.map((g, i) => (
                                        <Line key={g} type="monotone" dataKey={g} name={g}
                                            stroke={PALETTE[i % PALETTE.length]}
                                            strokeWidth={2} dot={false}
                                            activeDot={{ r: 5, onClick: (a, b) => handlePointClick(a, b, g), cursor: "pointer" }} />
                                    ))
                                ) : (
                                    <Line type="monotone" dataKey="value" name={seriesName}
                                        stroke="var(--chart-1, hsl(var(--primary)))" strokeWidth={2} dot={false}
                                        activeDot={{ r: 5, onClick: handlePointClick, cursor: "pointer" }} />
                                )}
                            </LineChart>
                        ) : isArea ? (
                            // iter-121.128c — Area chart path (single OR stacked
                            // multi-series). For multi-series, stackId='1' stacks
                            // the areas; remove it to overlay instead. We pick
                            // stacking by default because "Revenue + Cost stacked
                            // area" is the canonical user phrasing.
                            <AreaChart data={displayData} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}
                                onClick={(state) => {
                                    const lbl = state?.activeLabel ?? state?.activePayload?.[0]?.payload?.label;
                                    if (lbl != null) _emitDrill(lbl);
                                }}>
                                <CartesianGrid strokeDasharray="2 4" stroke="rgba(127,127,127,0.18)" />
                                <XAxis dataKey="label" fontSize={10} stroke="currentColor" {...tickFormatterProps} {...xAxisProps} />
                                <YAxis fontSize={10} stroke="currentColor" tickFormatter={(v) => formatKpi(v, fmt)} />
                                <Tooltip contentStyle={{ fontSize: 12 }} formatter={tooltipFmt} />
                                <Legend verticalAlign="top" height={20} iconType="rect" iconSize={10}
                                    wrapperStyle={{ fontSize: 11, paddingBottom: 4 }} />
                                {isMultiSeries ? (
                                    seriesDefs.map((s, i) => (
                                        <Area key={s.name || i} type="monotone" dataKey={s.name}
                                            name={s.name} stackId="1"
                                            stroke={s.color || PALETTE[i % PALETTE.length]}
                                            fill={s.color || PALETTE[i % PALETTE.length]}
                                            fillOpacity={0.55}
                                            /* ISSUE-FIX: Support drill-down on multi-series AreaChart */
                                            onClick={(entry) => handleBarClick(entry, s.name)} cursor="pointer" />
                                    ))
                                ) : hasGroups ? (
                                    groups.map((g, i) => (
                                        <Area key={g} type="monotone" dataKey={g} name={g}
                                            stackId="1"
                                            stroke={PALETTE[i % PALETTE.length]}
                                            fill={PALETTE[i % PALETTE.length]}
                                            fillOpacity={0.55}
                                            /* ISSUE-FIX: Support drill-down on grouped AreaChart */
                                            onClick={(entry) => handleBarClick(entry, g)} cursor="pointer" />
                                    ))
                                ) : (
                                    <Area type="monotone" dataKey="value" name={seriesName}
                                        stroke="var(--chart-1, hsl(var(--primary)))" fill="var(--chart-1, hsl(var(--primary)))" fillOpacity={0.45}
                                        /* ISSUE-FIX: Support drill-down on single AreaChart */
                                        onClick={handleBarClick} cursor="pointer" />
                                )}
                            </AreaChart>
                        ) : isHorizontalBar ? (
                            <BarChart layout="vertical" data={displayData} margin={{ top: 5, right: 8, left: 50, bottom: 28 }}>
                                <CartesianGrid strokeDasharray="2 4" stroke="rgba(127,127,127,0.18)" />
                                <XAxis type="number" fontSize={10} stroke="currentColor" tickFormatter={(v) => formatKpi(v, fmt)} />
                                <YAxis type="category" dataKey="label" fontSize={10} stroke="currentColor" width={48} />
                                <Tooltip contentStyle={{ fontSize: 12 }} formatter={tooltipFmt} />
                                <Legend verticalAlign="top" height={20} iconType="rect" iconSize={10}
                                    wrapperStyle={{ fontSize: 11, paddingBottom: 4 }} />
                                {isMultiSeries ? (
                                    seriesDefs.map((s, i) => (
                                        <Bar key={s.name || i} dataKey={s.name}
                                            name={s.name}
                                            fill={s.color || PALETTE[i % PALETTE.length]}
                                            radius={[0, 2, 2, 0]}
                                            /* ISSUE-FIX: Support stacking multiple series in horizontal BarChart if options/config specify stacked. Resolves the bug where stacked multi-series bar charts showed side-by-side bars. */
                                            stackId={widget.options?.stacked || widget.config?.stacked || widget.stacked ? "a" : undefined}
                                            /* ISSUE-FIX: Pass explicit series name to handleBarClick in multi-series horizontal BarChart */
                                            onClick={(entry) => handleBarClick(entry, s.name)} cursor="pointer" />
                                    ))
                                ) : hasGroups ? (
                                    groups.map((g, i) => (
                                        <Bar key={g} dataKey={g} name={g}
                                            fill={PALETTE[i % PALETTE.length]}
                                            stackId={widget.options?.stacked ? "a" : undefined}
                                            radius={[0, 2, 2, 0]}
                                            onClick={(entry) => handleBarClick(entry, g)} cursor="pointer" />
                                    ))
                                ) : (
                                    <Bar dataKey="value" name={seriesName}
                                        fill="var(--chart-1, hsl(var(--primary)))"
                                        radius={[0, 2, 2, 0]}
                                        onClick={handleBarClick} cursor="pointer" />
                                )}
                            </BarChart>
                        ) : (
                            <BarChart data={displayData} margin={{ top: 5, right: 8, left: 0, bottom: 28 }}>
                                <CartesianGrid strokeDasharray="2 4" stroke="rgba(127,127,127,0.18)" />
                                <XAxis dataKey="label" fontSize={10} stroke="currentColor"
                                    {...tickFormatterProps} {...xAxisProps} />
                                <YAxis fontSize={10} stroke="currentColor" tickFormatter={(v) => formatKpi(v, fmt)} />
                                <Tooltip contentStyle={{ fontSize: 12 }} formatter={tooltipFmt} />
                                <Legend verticalAlign="top" height={20} iconType="rect" iconSize={10}
                                    wrapperStyle={{ fontSize: 11, paddingBottom: 4 }} />
                                {/* iter-121.128c — Multi-series grouped bar. */}
                                {isMultiSeries ? (
                                    seriesDefs.map((s, i) => (
                                        <Bar key={s.name || i} dataKey={s.name}
                                            name={s.name}
                                            fill={s.color || PALETTE[i % PALETTE.length]}
                                            /* ISSUE-FIX: Support stacking multiple series in vertical BarChart if options/config specify stacked. Resolves the bug where stacked multi-series bar charts showed side-by-side bars. */
                                            stackId={widget.options?.stacked || widget.config?.stacked || widget.stacked ? "a" : undefined}
                                            /* ISSUE-FIX: Pass explicit series name to handleBarClick in multi-series vertical BarChart */
                                            onClick={(entry) => handleBarClick(entry, s.name)} cursor="pointer" />
                                    ))
                                ) : hasGroups ? (
                                    groups.map((g, i) => (
                                        <Bar key={g} dataKey={g} name={g}
                                            fill={PALETTE[i % PALETTE.length]}
                                            stackId={widget.options?.stacked ? "a" : undefined}
                                            onClick={(entry) => handleBarClick(entry, g)} cursor="pointer" />
                                    ))
                                ) : (
                                    <Bar dataKey="value" name={seriesName}
                                        fill="var(--chart-1, hsl(var(--primary)))"
                                        onClick={handleBarClick} cursor="pointer" />
                                )}
                            </BarChart>
                        )}
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
}


export function TablePrebuiltCard({
    widget, liveData, dashboardId, onHide, onRemoveFromPrebuilt, onResize,
    currentSize, onDashboardUpdated, onLoadMore, isLoadingMore,
}) {
    const rows = liveData?.rows || [];
    const columns = liveData?.columns || [];
    const tableHeight = 180 * (widget.rowspan || 2);
    const compact = tableHeight <= 220;
    // iter-121.129c — Multi-dimension headers. `liveData.dimensions` is
    // a list of `{alias, label}` carrying the group-by fields the table
    // was queried with — one per dimension the AI Builder declared. We
    // render ONE header column per dimension so a table grouped by
    // "channel × device" shows BOTH dimension columns (was: just the
    // first dimension as a single `entityLabel` column). Falls back to
    // the legacy single-entity shape when `dimensions` is empty.
    const dimensions = Array.isArray(liveData?.dimensions) && liveData.dimensions.length > 0
        ? liveData.dimensions
        : null;
    const entityLabel = liveData?.entityLabel || "Entity";

    // iter-121.51 — honour widget.format (currency, decimals). Raw style for
    // table cells (no K/M abbreviation, exact 2dp).
    const cellFmt = (v, colFormat) => {
        const fmt = (colFormat && typeof colFormat === "object") ? colFormat
            : (widget.format && typeof widget.format === "object") ? widget.format
                : { decimals: 2 };
        return formatNumberRaw(v, fmt);
    };

    const showTotal = widget.options?.show_total || widget.options?.show_totals || widget.options?.total_row || widget.options?.show_total_row || widget.options?.show_totals_row || (widget.config?.options && (widget.config.options.show_total || widget.config.options.show_totals || widget.config.options.total_row || widget.config.options.show_total_row || widget.config.options.show_totals_row));

    const totals = {};
    if (showTotal) {
        columns.forEach((c, ci) => {
            const colMeta = (widget.columns && widget.columns[ci]) || {};
            
            // ISSUE-FIX: Support COUNT_DISTINCT and calculated measures in TablePrebuiltCard totals calculation
            const getAggType = (formula, agg) => {
                if (agg) {
                    const a = String(agg).toLowerCase();
                    if (a === "avg" || a === "average" || a === "compound") return "avg";
                    if (a === "min" || a === "max") return a;
                    if (a === "count_distinct" || a === "distinct" || a === "count distinct" || a === "count_distinct_if") return "count_distinct";
                    if (a === "count" || a === "count_if") return "count";
                    return "sum";
                }
                if (!formula || typeof formula !== "string") return "sum";
                const f = formula.trim().toUpperCase();
                if (f.includes("/") || f.includes("*") || f.split("SUM").length > 2 || f.split("COUNT").length > 2) {
                    return "avg";
                }
                if (f.startsWith("AVG") || f.startsWith("AVERAGE")) return "avg";
                if (f.startsWith("MIN")) return "min";
                if (f.startsWith("MAX")) return "max";
                if (f.includes("DISTINCT") || f.startsWith("COUNT_DISTINCT")) return "count_distinct";
                if (f.startsWith("COUNT")) return "count";
                return "sum";
            };

            const aggType = getAggType(colMeta.formula, colMeta.agg);
            const values = [];

            for (const r of rows) {
                const val = r[c.alias];
                if (val !== undefined && val !== null && val !== "") {
                    const num = Number(val);
                    if (!isNaN(num)) {
                        values.push(num);
                    }
                }
            }

            const hasValues = rows.some(r => r[c.alias] !== undefined && r[c.alias] !== null && r[c.alias] !== "");
            const allNumeric = hasValues && values.length === rows.filter(r => r[c.alias] !== undefined && r[c.alias] !== null && r[c.alias] !== "").length;

            if (liveData?.grand_total && (liveData.grand_total[c.alias] !== undefined || liveData.grand_total[c.label] !== undefined)) {
                const totalVal = liveData.grand_total[c.alias] !== undefined ? liveData.grand_total[c.alias] : liveData.grand_total[c.label];
                totals[c.alias] = cellFmt(totalVal, c.format);
            } else if (allNumeric) {
                let result = 0;
                if (aggType === "avg" || aggType === "average" || aggType === "compound") {
                    const sum = values.reduce((a, b) => a + b, 0);
                    result = values.length ? (sum / values.length) : 0;
                } else if (aggType === "min") {
                    result = values.length ? Math.min(...values) : 0;
                } else if (aggType === "max") {
                    result = values.length ? Math.max(...values) : 0;
                } else if (aggType === "count_distinct") {
                    result = new Set(values).size;
                } else if (aggType === "count") {
                    result = values.length;
                } else {
                    result = values.reduce((a, b) => a + b, 0);
                }

                totals[c.alias] = cellFmt(result, c.format);
            } else {
                totals[c.alias] = "";
            }
        });
    }

    const handleScroll = (e) => {
        if (!onLoadMore || isLoadingMore || !liveData?.hasMore) return;
        const el = e.currentTarget;
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 180) {
            onLoadMore(widget);
        }
    };
    const headCellClass = compact ? "px-2 py-1 text-[9px] leading-4" : "px-3 py-2";
    const bodyCellClass = compact ? "px-2 py-1 leading-5" : "px-3 py-2";

    return (
        <div
            className="dash-card flex flex-col overflow-hidden"
            style={{ height: tableHeight, minHeight: tableHeight }}
            data-testid={`prebuilt-table-${widget.id}`}
        >
            <div className={`${compact ? "px-3 pt-2 pb-1" : "px-4 pt-4 pb-2"} flex items-center justify-between gap-2 shrink-0`}>
                <div className="text-[10px] font-mono uppercase tracking-wider opacity-70">{widget.title}</div>
                <div className="flex items-center gap-1">
                    <WidgetFilterOverrideToggle widget={widget} dashboardId={dashboardId} onDashboardUpdated={onDashboardUpdated} />
                    {/* iter-121.132 — Action menu. */}
                    {(onHide || onRemoveFromPrebuilt) && (
                        <WidgetActionMenu
                            widget={widget}
                            currentSize={currentSize}
                            onResize={onResize}
                            onHide={onHide}
                            onRemoveFromPrebuilt={onRemoveFromPrebuilt}
                        />
                    )}
                </div>
            </div>
            <div className="flex-1 min-h-0 overflow-auto scrollbar-thin pb-1" onScroll={handleScroll}>
                <table className="dash-table w-full text-sm">
                    <thead className="sticky top-0 z-10 bg-card border-y border-border">
                        <tr>
                            {dimensions ? (
                                dimensions.map((d) => (
                                    <th key={d.alias} className={`text-left eyebrow capitalize bg-secondary/30 ${headCellClass}`}>
                                        {String(d.label || d.field || "").replace(/_/g, " ")}
                                    </th>
                                ))
                            ) : (
                                <th className={`text-left eyebrow capitalize bg-secondary/30 ${headCellClass}`}>{entityLabel.replace(/_/g, " ")}</th>
                            )}
                            {columns.map((c) => (
                                <th key={c.alias} className={`text-right eyebrow bg-secondary/30 ${headCellClass}`}>{c.label}</th>
                            ))}
                        </tr>
                        {showTotal && (
                            <tr className="border-t border-border font-semibold" data-testid={`widget-${widget.id}-total-row`}>
                                {dimensions ? (
                                    dimensions.map((d, di) => (
                                        <th key={d.alias} className={`text-left text-foreground font-bold text-xs bg-card ${compact ? "px-2 py-1.5" : "px-3 py-2.5"}`}>
                                            {di === 0 ? "GRAND TOTAL" : ""}
                                        </th>
                                    ))
                                ) : (
                                    <th className={`text-left text-foreground font-bold text-xs bg-card ${compact ? "px-2 py-1.5" : "px-3 py-2.5"}`}>GRAND TOTAL</th>
                                )}
                                {columns.map((c) => (
                                    <th key={c.alias} className={`text-right text-foreground font-bold text-xs tabular-nums bg-card ${compact ? "px-2 py-1.5" : "px-3 py-2.5"}`}>
                                        {totals[c.alias]}
                                    </th>
                                ))}
                            </tr>
                        )}
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={i} className="border-b border-border last:border-b-0 hover:bg-secondary/20">
                                {dimensions ? (
                                    dimensions.map((d) => (
                                        <td key={d.alias}
                                            className={`${bodyCellClass} truncate max-w-[280px]`}
                                            title={r[d.alias]}>
                                            {r[d.alias] ?? "—"}
                                        </td>
                                    ))
                                ) : (
                                    <td className={`${bodyCellClass} truncate max-w-[280px]`} title={r.__entity}>{r.__entity ?? "—"}</td>
                                )}
                                {columns.map((c) => (
                                    <td key={c.alias} className={`${bodyCellClass} text-right tabular-nums font-mono text-xs`}>{cellFmt(r[c.alias], c.format)}</td>
                                ))}
                            </tr>
                        ))}
                        {isLoadingMore && (
                            <tr>
                                <td
                                    colSpan={(dimensions ? dimensions.length : 1) + columns.length}
                                    className="px-3 py-2 text-center text-[10px] font-mono uppercase tracking-wider text-muted-foreground"
                                >
                                    Loading more rows...
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}


function MultiSelectDropdown({ label, options, selected, onChange, loading, status, onOpen, testId }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const sel = Array.isArray(selected) ? selected : [];
    const opts = options || [];
    const filtered = search
        ? opts.filter((o) => String(o).toLowerCase().includes(search.toLowerCase()))
        : opts;
    const toggle = (o) => {
        if (sel.includes(o)) onChange(sel.filter((x) => x !== o));
        else onChange([...sel, o]);
    };
    // iter — default (nothing selected) reads "All" rather than a blank/empty
    // prompt so the chip never looks broken before the user picks a value.
    const summary = sel.length === 0
        ? "All"
        : sel.length === 1 ? String(sel[0]) : `${sel.length} selected`;
    const active = sel.length > 0;
    const isLoading = loading || status === "loading";
    return (
        <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o && onOpen) onOpen(); }}>
            <PopoverTrigger asChild>
                <button type="button"
                    className={`h-8 w-full rounded-sm border px-2 text-xs flex items-center justify-between gap-2 transition-colors ${
                        active
                            ? "border-primary bg-primary/10 text-primary font-medium hover:border-primary"
                            : "border-border bg-background hover:border-foreground/40"
                    }`}
                    data-testid={`${testId}-trigger`}
                    data-active={active ? "true" : "false"}>
                    <span className={`truncate ${active ? "" : "text-muted-foreground"}`}>{`${label}: ${summary}`}</span>
                    <ChevronDown className={`h-3 w-3 shrink-0 ${active ? "opacity-80" : "opacity-60"}`} />
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-0" align="start" data-testid={`${testId}-content`}>
                <div className="p-2 border-b border-border">
                    <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search…"
                        className="h-7 w-full rounded-sm border border-border bg-background px-2 text-xs"
                        data-testid={`${testId}-search`} />
                </div>
                <div className="max-h-64 overflow-y-auto">
                    {isLoading && (
                        <div className="px-3 py-4 text-xs text-muted-foreground italic" data-testid={`${testId}-loading`}>Loading values…</div>
                    )}
                    {!isLoading && status === "error" && (
                        <div className="px-3 py-4 text-xs text-destructive italic" data-testid={`${testId}-error`}>Unable to load values.</div>
                    )}
                    {!isLoading && status !== "error" && opts.length === 0 && (
                        <div className="px-3 py-4 text-xs text-muted-foreground italic" data-testid={`${testId}-empty`}>No values available.</div>
                    )}
                    {!isLoading && status !== "error" && opts.length > 0 && filtered.length === 0 && (
                        <div className="px-3 py-4 text-xs text-muted-foreground italic">No values match.</div>
                    )}
                    {!isLoading && status !== "error" && filtered.map((o) => {
                        const isSelected = sel.includes(o);
                        return (
                            <button key={String(o)} type="button" onClick={() => toggle(o)}
                                className={`flex items-center gap-2 w-full px-3 py-1.5 text-left text-xs hover:bg-secondary/40 ${isSelected ? "text-primary" : ""}`}
                                data-testid={`${testId}-opt-${o}`}>
                                <span className={`h-3 w-3 border rounded-sm flex items-center justify-center ${isSelected ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>
                                    {isSelected && <Check className="h-2.5 w-2.5" />}
                                </span>
                                <span className="truncate flex-1">{String(o)}</span>
                            </button>
                        );
                    })}
                </div>
                {sel.length > 0 && (
                    <div className="px-2 py-1.5 border-t border-border flex items-center justify-between">
                        <span className="text-[10px] font-mono uppercase tracking-wider opacity-70">{sel.length} selected</span>
                        <button type="button" onClick={() => onChange([])}
                            className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground hover:text-foreground"
                            data-testid={`${testId}-clear`}>
                            Clear
                        </button>
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
}


export function FilterControl({ filter, value, onChange, dashboardId, distinctUrl, scope, compact }) {
    const [dynamicOptions, setDynamicOptions] = useState(null);
    // iter — lazy-load status: 'idle' (not fetched yet) | 'loading' | 'loaded'
    // | 'error'. Distinct values are fetched when the dropdown OPENS, not on
    // mount, so a dashboard with many filter chips doesn't fire a burst of
    // /distinct calls before the user interacts.
    const [dynamicState, setDynamicState] = useState("idle");
    const isSourceFilter = filter?.id === "_source_kind" || filter?.id === "_source_id"
        || filter?.field === "_source_kind" || filter?.field === "_source_id";
    const hasFieldPath = !!(filter.field || filter.distinct_field || isSourceFilter);
    const shouldResolveDynamic = filter.type === "multi_select_dynamic"
        || (filter.type === "multi_select" && hasFieldPath);
    const scopeTenantId = scope?.isSA ? scope?.tenantId : undefined;
    const scopeOrgId = scope?.isSA ? scope?.orgId : undefined;

    // iter — reset the cached values if the filter's field/scope changes so a
    // re-open refetches against the correct field/scope.
    React.useEffect(() => {
        setDynamicOptions(null);
        setDynamicState("idle");
    }, [filter.id, filter.distinct_field, filter.field, filter.type, dashboardId, distinctUrl, scopeTenantId, scopeOrgId]);

    const loadDistinct = React.useCallback(() => {
        if (!shouldResolveDynamic || !dashboardId) return;
        // fetch once per (field/scope) — re-opening reuses the loaded set.
        if (dynamicState === "loading" || dynamicState === "loaded") return;
        const fieldName = filter.distinct_field
            || (isSourceFilter ? (filter.id || filter.field) : (filter.field || "").split(".").pop());
        if (!fieldName) return;
        const url = distinctUrl || `/dashboards/${dashboardId}/distinct`;
        const params = { field: fieldName, limit: 200 };
        if (scopeTenantId) params.scope_tenant_id = scopeTenantId;
        if (scopeOrgId) params.scope_org_id = scopeOrgId;
        setDynamicState("loading");
        api.get(url, { params })
            .then((r) => { setDynamicOptions(r.data?.values || []); setDynamicState("loaded"); })
            .catch(() => { setDynamicOptions([]); setDynamicState("error"); });
    }, [shouldResolveDynamic, dashboardId, dynamicState, filter.distinct_field, filter.field, filter.id, isSourceFilter, distinctUrl, scopeTenantId, scopeOrgId]);

    const label = filter.label || (filter.field || "filter").split(".").pop();
    if (filter.type === "date_range") {
        const v = value || {};
        if (compact) {
            const drActive = !!(v.from || v.to);
            return (
                <div className={`flex items-center gap-1.5 rounded-sm border px-1.5 py-0.5 transition-colors ${drActive ? "border-primary bg-primary/10" : "border-transparent"}`}>
                    <span className={`text-[10px] font-mono uppercase tracking-wider shrink-0 ${drActive ? "text-primary font-medium" : "opacity-70"}`}>{label}</span>
                    <input type="date" value={v.from || ""}
                        onChange={(e) => onChange({ ...v, from: e.target.value || null })}
                        className={`h-7 rounded-sm border bg-background px-1.5 text-[11px] ${drActive ? "border-primary/40" : "border-border"}`}
                        data-testid={`prebuilt-filter-${filter.id || filter.field}-from`} />
                    <span className="text-[10px] opacity-50">–</span>
                    <input type="date" value={v.to || ""}
                        onChange={(e) => onChange({ ...v, to: e.target.value || null })}
                        className={`h-7 rounded-sm border bg-background px-1.5 text-[11px] ${drActive ? "border-primary/40" : "border-border"}`}
                        data-testid={`prebuilt-filter-${filter.id || filter.field}-to`} />
                </div>
            );
        }
        return (
            <div className="space-y-1">
                <div className="text-[10px] font-mono uppercase tracking-wider opacity-70">{label}</div>
                <div className="flex gap-1.5">
                    <input type="date" value={v.from || ""}
                        onChange={(e) => onChange({ ...v, from: e.target.value || null })}
                        className="h-8 rounded-sm border border-border bg-background px-2 text-xs flex-1"
                        data-testid={`prebuilt-filter-${filter.id || filter.field}-from`} />
                    <input type="date" value={v.to || ""}
                        onChange={(e) => onChange({ ...v, to: e.target.value || null })}
                        className="h-8 rounded-sm border border-border bg-background px-2 text-xs flex-1"
                        data-testid={`prebuilt-filter-${filter.id || filter.field}-to`} />
                </div>
            </div>
        );
    }
    if (filter.type === "range") {
        // iter-119ag — numeric range filter rendered as a dual-handle
        // slider (Radix `Slider` with 2 thumbs) PAIRED with mirrored
        // from/to numeric inputs so power users can type exact values.
        // Slider step is auto-derived as ~100 steps across the visible
        // span so the UX feels precise without flooding the value with
        // decimal noise. Falls back gracefully to text inputs alone
        // when min/max are missing or equal.
        const v = value || {};
        const min = filter.min;
        const max = filter.max;
        const hasBounds = min != null && max != null && max > min;
        const span = hasBounds ? max - min : 0;
        const step = hasBounds
            ? (span >= 100 ? Math.max(1, Math.round(span / 100)) : Number((span / 100).toFixed(2)))
            : 1;
        const lo = v.from != null ? v.from : min;
        const hi = v.to != null ? v.to : max;
        const fmt = (n) => {
            if (n == null) return "";
            const abs = Math.abs(n);
            if (abs >= 1e6) return (n / 1e6).toFixed(2) + "M";
            if (abs >= 1e3) return (n / 1e3).toFixed(1) + "K";
            return Number.isInteger(n) ? String(n) : Number(n).toFixed(2);
        };
        return (
            <div className="space-y-2">
                <div className="text-[10px] font-mono uppercase tracking-wider opacity-70 flex items-center gap-2">
                    <span>{label}</span>
                    {hasBounds && (
                        <span className="font-normal opacity-60">
                            {fmt(lo)} – {fmt(hi)}
                        </span>
                    )}
                </div>
                {hasBounds && (
                    <Slider
                        min={min}
                        max={max}
                        step={step}
                        value={[lo, hi]}
                        onValueChange={(vals) => onChange({ from: vals[0], to: vals[1] })}
                        className="mt-1.5"
                        data-testid={`prebuilt-filter-${filter.id || filter.field}-slider`}
                    />
                )}
                <div className="flex gap-1.5">
                    <input
                        type="number"
                        placeholder={min != null ? `≥ ${fmt(min)}` : "Min"}
                        value={v.from ?? ""}
                        onChange={(e) => onChange({ ...v, from: e.target.value === "" ? null : Number(e.target.value) })}
                        className="h-7 rounded-sm border border-border bg-background px-2 text-xs flex-1 font-mono tabular-nums"
                        data-testid={`prebuilt-filter-${filter.id || filter.field}-from`}
                    />
                    <input
                        type="number"
                        placeholder={max != null ? `≤ ${fmt(max)}` : "Max"}
                        value={v.to ?? ""}
                        onChange={(e) => onChange({ ...v, to: e.target.value === "" ? null : Number(e.target.value) })}
                        className="h-7 rounded-sm border border-border bg-background px-2 text-xs flex-1 font-mono tabular-nums"
                        data-testid={`prebuilt-filter-${filter.id || filter.field}-to`}
                    />
                </div>
            </div>
        );
    }
    if (filter.type === "multi_select" || filter.type === "multi_select_dynamic") {
        const sel = Array.isArray(value) ? value : [];
        let opts;
        if (filter.type === "multi_select_dynamic") {
            opts = dynamicOptions ?? [];
        } else if (shouldResolveDynamic) {
            // iter-104: static options ∩ live values for this dashboard.
            // Before the first open (idle) or while loading we show nothing
            // so users never see leaked static values; once loaded we
            // intersect the static contract with the live set.
            const stat = filter.options || [];
            if (dynamicOptions === null || dynamicOptions.length === 0) {
                opts = [];
            } else if (stat.length === 0) {
                // No static contract — surface everything the data has.
                opts = dynamicOptions;
            } else {
                const live = new Set(dynamicOptions);
                opts = stat.filter((o) => live.has(o));
                // Surface live values not in the static options too — a
                // newly-added enum (e.g. status="archived") shows up
                // automatically without a template re-save.
                dynamicOptions.forEach((d) => { if (!stat.includes(d)) opts.push(d); });
            }
        } else {
            opts = filter.options || [];
        }
        // iter — map lazy-load state → dropdown status. Non-dynamic (static
        // options only) is always "loaded".
        const status = shouldResolveDynamic ? dynamicState : "loaded";
        const onOpen = shouldResolveDynamic ? loadDistinct : undefined;
        const loading = status === "loading";
        if (compact) {
            return (
                <div className="w-[150px] shrink-0">
                    <MultiSelectDropdown
                        label={label}
                        options={opts}
                        selected={sel}
                        onChange={onChange}
                        loading={loading}
                        status={status}
                        onOpen={onOpen}
                        testId={`prebuilt-filter-${filter.id || filter.field}`}
                    />
                </div>
            );
        }
        return (
            <div className="space-y-1">
                <div className="text-[10px] font-mono uppercase tracking-wider opacity-70">
                    {label}{filter.type === "multi_select_dynamic" && opts.length > 0 && ` · ${opts.length}`}
                </div>
                <MultiSelectDropdown
                    label={label}
                    options={opts}
                    selected={sel}
                    onChange={onChange}
                    loading={loading}
                    status={status}
                    onOpen={onOpen}
                    testId={`prebuilt-filter-${filter.id || filter.field}`}
                />
            </div>
        );
    }
    return (
        <div className="space-y-1">
            <div className="text-[10px] font-mono uppercase tracking-wider opacity-70">{label}</div>
            <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)}
                placeholder={`Filter by ${label.toLowerCase()}…`}
                className="h-8 rounded-sm border border-border bg-background px-2 text-xs w-full"
                data-testid={`prebuilt-filter-${filter.id || filter.field}-input`} />
        </div>
    );
}